import type {
  BookingPurchaseDialogData,
  BookingPurchaseDocumentLink,
  BookingPurchaseWarning,
} from './BookingPurchaseDialog.types';
import type { ActionButtonData } from '../action-button/ActionButton';
import { t } from '../../services/locale';

export type DialogProjectionState = {
  success: boolean;
  error: boolean;
  title: string;
  isUserLogIn: boolean;
  isAllowToBook: boolean;
  showPrice: boolean;
  userHasCredits: boolean;
  isGoingToWaitingList: boolean;
  priceToPay: number | null;
  creditsToPay: number;
  outstandingDocuments: Array<{ name?: string; documentUrl?: string }>;
  errors: string[];
  messagesData: Record<string, unknown>;
  isMissingProfileDetails: boolean;
  showPayment: boolean;
};

function unescapeAmpersand(value: string): string {
  return value.replaceAll('&amp;', '&');
}

function showsPriceRow(state: DialogProjectionState): boolean {
  return !!state.showPrice || !!state.isUserLogIn;
}

function buildNoCreditsPriceLabel(state: DialogProjectionState): string | null {
  if (state.priceToPay === 0) {
    return t('WEB_FREE_EVENT');
  }
  if (state.priceToPay === null && state.isUserLogIn) {
    return t('WEB_NOT_ELIGIBLE');
  }
  return null;
}

export function resolveDialogView(state: {
  success: boolean;
  error: boolean;
}): BookingPurchaseDialogData['view'] {
  if (state.success && !state.error) {
    return 'success';
  }
  if (!state.success && state.error) {
    return 'error';
  }
  return 'main';
}

export function buildPriceLabel(state: DialogProjectionState): string | null {
  if (!showsPriceRow(state)) {
    return null;
  }
  if (state.userHasCredits) {
    return state.isUserLogIn ? `${state.creditsToPay} ${t('WEB_CREDITS')}` : null;
  }
  return buildNoCreditsPriceLabel(state);
}

export function buildPriceLabelId(state: DialogProjectionState): string | null {
  if (state.userHasCredits) {
    return null;
  }
  if (state.priceToPay === null && state.isUserLogIn) {
    return 'not-eligible-status-classes';
  }
  return null;
}

function hasOutstandingDocuments(state: DialogProjectionState): boolean {
  return !!state.outstandingDocuments && state.outstandingDocuments.length > 0;
}

function showsWaitingListWarning(state: DialogProjectionState): boolean {
  const priceResolved = state.priceToPay !== null || state.userHasCredits;
  return priceResolved && !!state.isGoingToWaitingList && !hasOutstandingDocuments(state);
}

export function buildWarnings(
  state: DialogProjectionState,
  handlers: { onOpenProfile?: () => void }
): BookingPurchaseWarning[] {
  const warnings: BookingPurchaseWarning[] = [];

  if (showsWaitingListWarning(state)) {
    warnings.push({
      message: t('WEB_WAITING_LIST_WARNING'),
      modifier: 'waitlist',
    });
  }
  if (hasOutstandingDocuments(state) && !state.isMissingProfileDetails) {
    warnings.push({
      message: t('WEB_OUTSTANDING_DOCUMENTS_WARNING'),
      modifier: 'documents',
    });
  }
  if (state.isMissingProfileDetails) {
    warnings.push({
      message: t('WEB_OUTSTANDING_PROFILE_INFO_WARNING'),
      modifier: 'documents',
      onClick: handlers.onOpenProfile,
    });
  }

  return warnings;
}

export function buildDocumentLinks(
  state: DialogProjectionState,
  onOpenDocument: (document: { name?: string; documentUrl?: string }) => void
): BookingPurchaseDocumentLink[] {
  if (!state.isUserLogIn) {
    return [];
  }
  if (!state.isAllowToBook) {
    return [];
  }
  if (!hasOutstandingDocuments(state)) {
    return [];
  }

  return state.outstandingDocuments.map((document) => {
    const name = unescapeAmpersand(t(document.name || ''));
    return {
      label: `${t('WEB_SIGN_DOCUMENT')} ${name}`,
      onClick: () => onOpenDocument(document),
    };
  });
}

export function buildResponseTitle(
  view: BookingPurchaseDialogData['view'],
  state: DialogProjectionState
): string {
  if (view !== 'success') {
    return t('WEB_YOU_FAILED_BOOKING');
  }
  return t(
    state.isGoingToWaitingList ? 'WEB_YOU_SUCCESSFULLY_JOINED_WAITING_LIST' : 'WEB_YOU_SUCCESSFULLY_BOOKED'
  );
}

export function buildResponseErrors(state: DialogProjectionState): string[] {
  return (state.errors || []).map((error) => t(error, state.messagesData));
}

type FooterButtonArgs = {
  button: 'primary' | 'signUp' | 'done' | 'tryAgain';
  priceToPay?: number | null;
  userHasCredits?: boolean;
  goingToWaitingList?: boolean;
  transactionInProgress?: boolean;
  isPriceLoading?: boolean;
  isPriceBreakdownLoading?: boolean;
  isMissingProfileDetailsBlockingBooking?: boolean;
  loadingOutstandingDocuments?: boolean;
  isEagreementsFlagResolved?: boolean;
  areDocumentsSigned?: boolean;
  onClick: () => void;
};

function isFooterButtonDisabled(args: FooterButtonArgs): boolean {
  const pending = [
    args.transactionInProgress,
    args.isPriceLoading,
    args.isPriceBreakdownLoading,
    args.isMissingProfileDetailsBlockingBooking,
    args.loadingOutstandingDocuments,
  ].some(Boolean);
  if (args.button !== 'primary') {
    return pending;
  }
  return pending || !args.isEagreementsFlagResolved || !args.areDocumentsSigned;
}

function footerLabelKey(args: FooterButtonArgs): string {
  const buttonLabels: Partial<Record<FooterButtonArgs['button'], string>> = {
    signUp: 'WEB_SIGN_UP_BOOK',
    done: 'WEB_DONE',
    tryAgain: 'WEB_TRY_AGAIN',
  };
  if (buttonLabels[args.button]) {
    return buttonLabels[args.button]!;
  }
  if (args.priceToPay === null && !args.userHasCredits) {
    return 'WEB_NOT_ELIGIBLE_TO_BOOK';
  }
  return args.goingToWaitingList ? 'WEB_JOIN_WAITING_LIST' : 'WEB_BOOK';
}

export function buildBookFooterButton(args: FooterButtonArgs): ActionButtonData {
  const meta: Record<string, { id?: string; dataTestId?: string }> = {
    primary: { id: 'class-booking-confirm' },
    signUp: {},
    done: { id: 'class-booking-done' },
    tryAgain: {},
  };

  const disabled = isFooterButtonDisabled(args);
  const variant = args.button === 'primary' && disabled ? 'disabled' : 'primary';

  return {
    ...meta[args.button],
    label: t(footerLabelKey(args)),
    variant,
    onClick: args.onClick,
  };
}
